import { useEffect, useRef } from "react";
import { AlertTriangle, Ban, Pencil } from "lucide-react";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn, formatScore } from "@/lib/utils";
import type { CyFunLevel } from "@/types/risk-analysis";
import {
  computeSectorIdentifier,
  type ModificationRow as ModificationRowType,
} from "@/utils/riskMatrixModifications";

type SectorReviewCardProps = {
  sectorName: string;
  sectorCode: string;
  score: number | null;
  cyfunLevel: CyFunLevel | null;
  modifications: ModificationRowType[];
  isSkipped: boolean;
  isIncomplete?: boolean;
  justifications: Record<string, string>;
  onJustificationChange: (modId: string, value: string) => void;
  onEdit?: () => void;
  onAllJustified?: (allJustified: boolean) => void;
  showValidation?: boolean;
};

function ModificationRow({
  description,
  modId,
  value,
  hasError,
  onChange,
}: {
  description: string;
  modId: string;
  value: string;
  hasError: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2 text-xs py-1 md:gap-4 md:text-sm md:py-1.5">
      <span className="min-w-0">{description}</span>
      <div className="flex items-center gap-3 shrink-0">
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger
            className={cn("w-20 h-8", hasError && "border-red-500")}
          >
            <SelectValue placeholder="-" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="da">Da</SelectItem>
            <SelectItem value="nu">Nu</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-[10px] text-muted-foreground w-12 text-right md:text-xs md:w-16">
          {modId}
        </span>
      </div>
    </div>
  );
}

function ModificationsSection({
  modifications,
  justifications,
  unjustifiedCount,
  showValidation,
  onJustificationChange,
}: {
  modifications: ModificationRowType[];
  justifications: Record<string, string>;
  unjustifiedCount: number;
  showValidation?: boolean;
  onJustificationChange: (modId: string, value: string) => void;
}) {
  if (modifications.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">Valori implicite DNSC</p>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium italic md:text-sm">
          Au fost identificate {modifications.length} modificări care trebuie
          justificate!
        </p>
        <span className="hidden md:inline text-xs text-muted-foreground shrink-0">
          Atașez justificare
        </span>
      </div>
      <div className="divide-y">
        {modifications.map((mod) => (
          <ModificationRow
            key={mod[2]}
            description={mod[0]}
            modId={mod[2]}
            value={justifications[mod[2]] ?? ""}
            hasError={!!showValidation && !justifications[mod[2]]}
            onChange={(v) => onJustificationChange(mod[2], v)}
          />
        ))}
      </div>
      {showValidation && unjustifiedCount > 0 && (
        <p className="text-sm text-red-600 mt-2">
          Selectați Da/Nu pentru toate cele {unjustifiedCount} modificări
          nejustificate.
        </p>
      )}
    </div>
  );
}

function SectorSummary({
  sectorCode,
  score,
  cyfunLevel,
}: {
  sectorCode: string;
  score: number;
  cyfunLevel: CyFunLevel;
}) {
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-2 text-xs md:p-4 md:text-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <p>
            <strong>
              Scorul general: {formatScore(score)}. Categoria de cerințe:{" "}
              {cyfunLevel}
            </strong>
          </p>
          <p>
            Implementați categoria de cerințe de securitate cibernetică - nivel{" "}
            {cyfunLevel}.
          </p>
        </div>
      </div>
    </div>
  );
}

export function SectorReviewCard({
  sectorName,
  sectorCode,
  score,
  cyfunLevel,
  modifications,
  isSkipped,
  isIncomplete,
  justifications,
  onJustificationChange,
  onEdit,
  onAllJustified,
  showValidation,
}: SectorReviewCardProps) {
  const unjustifiedCount =
    isSkipped || isIncomplete
      ? 0
      : modifications.filter((mod) => !justifications[mod[2]]).length;

  const onAllJustifiedRef = useRef(onAllJustified);
  onAllJustifiedRef.current = onAllJustified;

  useEffect(() => {
    onAllJustifiedRef.current?.(unjustifiedCount === 0);
  }, [unjustifiedCount]);

  if (isSkipped) {
    return (
      <Card className="opacity-60">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2">
            <span className="font-bold text-sm md:text-base">{sectorName}</span>
            <span className="text-sm text-muted-foreground">-</span>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 pt-0 md:pt-0">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Ban className="h-4 w-4" />
            <span>Sector omis - nu a fost analizat</span>
          </div>
          {onEdit && (
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Pencil className="h-4 w-4 mr-2" />
              Analizează sectorul
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  if (isIncomplete) {
    return (
      <Card className="border-amber-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2">
            <span className="font-bold text-sm md:text-base">{sectorName}</span>
            <span className="text-sm text-muted-foreground">-</span>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 pt-0 md:pt-0">
          <div className="flex items-start gap-2 text-sm text-amber-700">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              Acest sector nu a fost analizat încă. Completați analiza pentru a
              vedea rezultatele.
            </span>
          </div>
          {onEdit && (
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Pencil className="h-4 w-4 mr-2" />
              Completează analiza
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  const sectorIdentifier =
    score !== null
      ? computeSectorIdentifier(sectorCode, modifications.length, score)
      : "-";

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <span className="font-bold text-sm md:text-base">{sectorName}</span>
          <span className="text-sm text-muted-foreground">
            {sectorIdentifier}
          </span>
        </div>
      </CardHeader>

      <CardContent className="space-y-3 pt-0 md:space-y-4 md:pt-0">
        <ModificationsSection
          modifications={modifications}
          justifications={justifications}
          unjustifiedCount={unjustifiedCount}
          showValidation={showValidation}
          onJustificationChange={onJustificationChange}
        />

        <Separator />

        {score !== null && cyfunLevel !== null && (
          <SectorSummary
            sectorCode={sectorCode}
            score={score}
            cyfunLevel={cyfunLevel}
          />
        )}
      </CardContent>
    </Card>
  );
}
